import { createContext, useContext } from "react";
import { normalizePathMode } from "@/lib/luagate/path-mode";
import { pathItemKind, type PathItemKind } from "@/lib/luagate/path-item-kind";
import { LgKindTag, type LgKind } from "./LgKindTag";
import { LgBadge } from "./LgTag";
import { usePathHints } from "../luagate-path/path-hints-context";

/*
学習パスの項目 1 本の種類の札 (読む、手を動かす、やってみる、宿題、道場、実践など) と、学び方の札 (読むだけ、任意)。
地図で章を開いた一覧、一覧のタブ、項目のページの頭と学習目次が、どれもこの部品を置く。
種類の決め方は lib/luagate/path-item-kind.ts。字と墨の手描きの絵で 1 つの札にし、色は付けない (黄は使わない)
*/

/** 見ている人がクラウドエディターで手を動かせる項目の id。画面の上で 1 回だけ入れる。入れなければ誰も「手を動かす」にならない */
export const LgHandsOnItemsContext = createContext<ReadonlySet<string>>(new Set());

const KIND_TAG: Record<PathItemKind, LgKind> = {
  practice: "practice",
  dojo: "dojo",
  video: "video",
  quiz: "quiz",
  "hands-on": "hands-on",
  task: "task",
  homework: "homework",
  reading: "reading",
};

export type LgItemKindProps = {
  /** team_curriculums の id。手を動かす項目かを引く */
  id?: string | null;
  itemType: string;
  title?: string;
  courseSlug?: string | null;
  pathMode?: string | null;
  className?: string;
};

export function LgItemKind({ id, itemType, title, courseSlug, pathMode, className }: LgItemKindProps) {
  const handsOn = useContext(LgHandsOnItemsContext);
  // 並びの正本が種類を渡したとき (手元の v2 の見本だけ) はそれを使う。手を動かす人の札は今までどおり先に効かせる
  const hints = usePathHints();
  const hinted = id ? hints?.kinds?.[id] : undefined;
  const isHandsOn = !!id && handsOn.has(id);
  const kind = hinted ? (isHandsOn ? "hands-on" : hinted) : pathItemKind({ id, itemType, title, courseSlug }, isHandsOn);
  const mode = normalizePathMode(pathMode);
  return (
    <span className={`lg-item-kind${className ? ` ${className}` : ""}`} data-item-kind={kind}>
      <LgKindTag kind={KIND_TAG[kind]} />
      {/* 種類が読むなら「読むだけ」は重ねない */}
      {mode === "read" && kind !== "reading" ? (
        <span data-path-mode="read">
          <LgBadge>読むだけ</LgBadge>
        </span>
      ) : null}
      {mode === "optional" ? (
        <span data-path-mode="optional">
          <LgBadge>任意</LgBadge>
        </span>
      ) : null}
    </span>
  );
}
